/* eslint-disable sonarjs/cognitive-complexity */
import moment from 'moment';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { getLocalStorage as getLocalStorageItem } from './localStorageManagement';

import ConfirmationLabels, {
  CHECKOUT_LABELS,
} from '../components/features/CnC/Confirmation/views/Confirmation.labels';

export const toShortDayName = (fullDayName) => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const index = days.indexOf(fullDayName?.toLowerCase());
  if (index === -1) return fullDayName; // Return the full name if it's not a valid day name
  return moment().day(index).format('ddd');
};

// Helper function to parse the time from the date-time string
export const parseTime = (dateTimeStr) => {
  const timePart = dateTimeStr?.split(' ')[1];
  const [hour, minute, second] = timePart?.split(':')?.map(Number) || [];
  return hour + minute / 60 + second / 3600;
};

// Helper function to compare dates (ignoring time)
export const isSameDate = (dateTimeStr, now) => {
  const datePart = dateTimeStr?.split(' ')[0];
  const [year, month, day] = datePart?.split('-')?.map(Number) || [];
  const date = new Date(year, month - 1, day);
  return date.toDateString() === now.toDateString();
};

const getMonthAndDate = (dateString) => moment(dateString).format('MMM D');

// Helper function to find the next open day
export const findNextOpenDay = (startIndex, regularHours) => {
  for (let i = startIndex; i < startIndex + regularHours.length; i += 1) {
    const index = i % regularHours.length;
    const { dayName, openIntervals, isClosed } = regularHours[index];

    if (!isClosed) {
      const openDate = openIntervals[0].fromHour.split(' ')[0];
      const openDayNumber = new Date(openDate).getDay();
      return {
        dayName: toShortDayName(dayName),
        openDate: getMonthAndDate(openDate),
        openDayNumber,
      };
    }
  }
  // All days are closed. Return the next day after the last day in regularHours.
  const lastDay = regularHours[regularHours.length - 1];
  const lastDayDate = new Date(lastDay.openIntervals[0]?.fromHour?.split(' ')[0]);
  const nextDayDate = new Date(lastDayDate);
  nextDayDate.setDate(lastDayDate.getDate() + 1); // Add one day to the last day date

  return {
    dayName: toShortDayName(nextDayDate.toLocaleDateString('en-US', { weekday: 'long' })),
    openDate: getMonthAndDate(nextDayDate),
    openDayNumber: nextDayDate.getDay(),
  };
};

export const getStoreInfo = () => {
  return JSON.parse(getLocalStorageItem('defaultStore'));
};

const storeClosedToday = (i, regularHours) => {
  // Store is open today but the gap is less than the specified hours
  const nextDayIndex = (i + 1) % regularHours.length;

  // Check if store is open tomorrow
  if (!regularHours[nextDayIndex].isClosed) {
    return {
      dayName: 'Tomorrow',
    };
  }

  // Find the next available open day
  return findNextOpenDay(nextDayIndex, regularHours);
};

const getNextDayDate = (
  isClosed,
  toTime,
  currentTimeInHours,
  hoursBeforeClose,
  openIntervals,
  i,
  regularHours
) => {
  const checkoutLabels = getLabelFromCopyOrFromTranslation(CHECKOUT_LABELS, ConfirmationLabels);

  const { orderConfirmation } = checkoutLabels;
  if (!isClosed) {
    if (toTime - currentTimeInHours > hoursBeforeClose) {
      const openDate = openIntervals[0].fromHour.split(' ')[0];
      return {
        dayName: renderLabel(orderConfirmation?.lbl_confirmation_today, 'lbl_confirmation_today'),
        openDateForSPC: getMonthAndDate(openDate),
      };
    }

    // Store is open today but the gap is less than the specified hours
    const nextDayIndex = (i + 1) % regularHours.length;

    // Check if store is open tomorrow
    if (!regularHours[nextDayIndex].isClosed) {
      const { openDate } = findNextOpenDay(nextDayIndex, regularHours);
      return {
        dayName: renderLabel(
          orderConfirmation?.lbl_confirmation_tomorrow,
          'lbl_confirmation_tomorrow'
        ),
        openDateForSPC: openDate,
      };
    }

    // Find the next available open day
    return findNextOpenDay(nextDayIndex, regularHours);
  }
  return storeClosedToday(i, regularHours);
};

export const getNextOpenDay = ({ hoursBeforeClose, storeInformation }) => {
  const storeInfo = getStoreInfo();
  if (!storeInfo) return null;
  const { regularHours: regHrs } = storeInfo.hours;
  let regularHours = regHrs;
  if (
    storeInformation &&
    storeInfo?.basicInfo?.storeName !== storeInformation?.basicInfo?.storeName
  ) {
    regularHours = storeInformation?.hours?.regularAndHolidayHours;
  }
  const now = new Date();
  const currentTimeInHours = now.getHours() + now.getMinutes() / 60; // Current time in hours

  for (let i = 0; i < regularHours.length; i += 1) {
    const { openIntervals, isClosed } = regularHours[i];

    const toTime = parseTime(openIntervals[0]?.toHour);

    if (isSameDate(openIntervals[0]?.fromHour, now)) {
      return getNextDayDate(
        isClosed,
        toTime,
        currentTimeInHours,
        hoursBeforeClose,
        openIntervals,
        i,
        regularHours
      );
    }
  }

  return {}; // This line should never be reached.
};
